import { message } from "antd";
import { all } from "axios";
export const validatePreviousRoomsForNewDates = (
  previousSelectedRooms,
  newAvailabilityData,
  roomTypes // Currently unused but kept for future enhancements
)=>{
  
    const availableRooms = {};
    const unavailableRooms = [];
    if (isNoRoomSelectedPreviously(previousSelectedRooms)) {
    return { availableRooms, unavailableRooms };
  }

  // If no new availability data, all rooms become unavailable
  if (!newAvailabilityData) {
    markAllPreviousRoomsAsUnavailable(previousSelectedRooms, unavailableRooms);
    return { availableRooms, unavailableRooms };
  }
  
  const allCurrentlyAvailableRoomIds = findAllAvailableRoomIds(newAvailabilityData);


  for(const roomTypeName in previousSelectedRooms){
    availableRooms[roomTypeName] = 
    filterPreviousListWithCurrentAvailability(
      previousSelectedRooms[roomTypeName], 
      allCurrentlyAvailableRoomIds
    );
    updateListOfNonAvailableRooms(
      previousSelectedRooms[roomTypeName], 
      allCurrentlyAvailableRoomIds,
      unavailableRooms
    );
  }

  return {availableRooms,unavailableRooms};
}

function findAllAvailableRoomIds(newAvailabilityData) {
  const allAvailableRoomIds = [];
  
  for(const roomTypeAvailability of newAvailabilityData){
    const availableRoomsOfType = roomTypeAvailability.rooms || [];

    // Check if this room type has selected rooms
    if (availableRoomsOfType.length === 0) {
      continue;
    }
    for(const availableRoom of availableRoomsOfType){
          allAvailableRoomIds.push(availableRoom.id);
    }
  }
  return allAvailableRoomIds;
}

function isNoRoomSelectedPreviously(previousSelectedRooms) {
  return !previousSelectedRooms || Object.keys(previousSelectedRooms).length === 0;
}

function markAllPreviousRoomsAsUnavailable(previousSelectedRooms, unavailableRooms) {
  for (const roomTypeName in previousSelectedRooms) {
      const selectedRoomsOfType = previousSelectedRooms[roomTypeName];
      unavailableRooms.push(...selectedRoomsOfType.map(room => ({
        ...room,
        roomTypeName,
        reason: `No availability data for ${roomTypeName}`
      })));
    }
  }

function filterPreviousListWithCurrentAvailability(
  previousSelectedRoomsOfRoomType, 
  allCurrentlyAvailableRoomIds
) {
      return previousSelectedRoomsOfRoomType?.filter(
        (room) => room && room.id && allCurrentlyAvailableRoomIds.includes(room.id)
      ) || [];
}

function updateListOfNonAvailableRooms(
  previousSelectedRoomsOfRoomType,
  allCurrentlyAvailableRoomIds, 
  unavailableRooms
) {
  previousSelectedRoomsOfRoomType?.forEach((room) => {
      if (!room || !room.id) {
        return; // Skip invalid room objects
      }
      if (!allCurrentlyAvailableRoomIds.includes(room.id)) {
        unavailableRooms.push({
          ...room,
          roomTypeName: room.name || "Unknown",
          reason: `Room ${room.name} not available for selected dates`
        });
      }
    });
  }

// Notification queue to prevent race conditions
let notificationQueue = [];
let isProcessingNotifications = false;
let notificationTimeouts = [];

const processNotificationQueue = () => {
  if (isProcessingNotifications || notificationQueue.length === 0) return;
  
  isProcessingNotifications = true;
  message.destroy(); // Clear existing messages
  
  // Clear any existing timeouts
  notificationTimeouts.forEach(timeout => clearTimeout(timeout));
  notificationTimeouts = [];
  
  // Process all queued notifications with small delays
  let delay = 0;
  while (notificationQueue.length > 0) {
    const notification = notificationQueue.shift();
    
    if (delay === 0) {
      // Execute first notification immediately
      notification();
    } else {
      // Queue subsequent notifications with delays
      const timeout = setTimeout(notification, delay);
      notificationTimeouts.push(timeout);
    }
    
    delay += 100; // 100ms delay between notifications
  }
  
  // Reset processing flag after all notifications are scheduled
  const resetTimeout = setTimeout(() => {
    isProcessingNotifications = false;
  }, delay);
  notificationTimeouts.push(resetTimeout);
};

/**
 * Shows user notifications for rooms that became unavailable after date change
 * @param {Array} unavailableRooms - Array of rooms that are no longer available
 * @param {Array} availableRooms - Object of rooms that were preserved
 */
export const showUnavailableRoomNotifications = (unavailableRooms, availableRooms = {}) => {
  // Queue warning notifications
  if (unavailableRooms.length > 0) {
    if (unavailableRooms.length === 1) {
      notificationQueue.push(() => {
        message.warning(
          `Room ${unavailableRooms[0].name} is not available for the selected dates and has been removed from your selection.`,
          5
        );
      });
    } else {
      const roomNames = unavailableRooms.map(room => room.name).slice(0, 3).join(', ');
      const moreText = unavailableRooms.length > 3 ? ` and ${unavailableRooms.length - 3} more` : '';
      
      notificationQueue.push(() => {
        message.warning(
          `${unavailableRooms.length} rooms (${roomNames}${moreText}) are not available for the selected dates and have been removed.`,
          6
        );
      });
    }
  }

  // Calculate preserved rooms count
  const preservedCount = Object.keys(availableRooms).reduce((sum, type) => sum + availableRooms[type].length, 0);

  // Queue success notifications
  if (preservedCount > 0) {
    const successMessage = unavailableRooms.length > 0 
      ? ` ${preservedCount} room${preservedCount === 1 ? '' : 's'} are available from your previous selection.`
      : `All ${preservedCount} room${preservedCount === 1 ? '' : 's'} are available for the new dates.`;
    
    notificationQueue.push(() => {
      message.success(successMessage, 4);
    });
  } else if (unavailableRooms.length === 0) {
    
  }
  
  // Process all queued notifications
  processNotificationQueue();
};

/**
 * Shows success notification when all rooms are preserved
 * @param {Object} availableRooms - Preserved rooms by room type
 */
export const showAllRoomsPreservedNotification = (availableRooms) => {
  const totalRooms = Object.keys(availableRooms).reduce((sum, type) => sum + availableRooms[type].length, 0);
  
  if (totalRooms > 0) {
    notificationQueue.push(() => {
      message.success(
        `All ${totalRooms} room${totalRooms === 1 ? '' : 's'} are available for the new dates.`,
        4
      );
    });
    
    // Process the notification
    processNotificationQueue();
  }
};